<script setup lang="ts">
/* eslint-disable no-undef */
/**
 * QxVerify Product Route Page
 *
 * Static route for QxVerify product landing page.
 * Queries markdown content from /content/products/verify.md
 * and renders using ProductPageTemplate component.
 *
 * Path: /verify
 * Content Source: content/products/verify.md
 * Integration: Nuxt Content + ProductPageTemplate + BookingsCTA
 */

import BookingsCTA from '../../components/cta/BookingsCTA.vue'
import FormFallbackCTA from '../../components/cta/FormFallbackCTA.vue'
import {
  buildBreadcrumbJsonLd,
  buildProductJsonLd,
  createJsonLdScript,
  toAbsoluteUrl,
} from '../../composables/useStructuredData'

/**
 * Access app configuration for bookings URL
 */
const appConfig = useAppConfig()

/**
 * Bookings API availability flag
 * In production, this would be connected to health check endpoint
 * For MVP: Toggle manually via browser console: window.__bookingsAvailable = false
 */
const bookingsAvailable = ref(true)

// Allow runtime override for testing (SSR-safe)
if (typeof window !== 'undefined') {
  (window as any).__bookingsAvailable = bookingsAvailable
}

/**
 * Product content interface matching markdown frontmatter schema
 */
interface ProductContent {
  title: string;
  description: string;
  image?: string;
  keywords?: string[];
  publishedAt?: string;
  body?: {
    children: Array<any>;
  };
}

/**
 * Query markdown content for QxVerify product.
 * Uses useAsyncData for SSG compatibility and caching.
 * Handles prerendering context where queryContent may not be available.
 */
const { data: verifyData } = await useAsyncData('verify-product', async () => {
  try {
    // @ts-expect-error - Nuxt Content auto-import
    const queryContentFn = typeof queryContent !== 'undefined' ? queryContent : null
    if (queryContentFn) {
      return await queryContentFn<ProductContent>('products/verify').findOne()
    }
    return null
  } catch (err) {
    // Handle prerendering context where queryContent is not available
    console.warn('Failed to load content during prerendering:', err)
    return null
  }
});

const CANONICAL_URL = 'https://qx.qiksolve.com/verify';
const SOCIAL_IMAGE_FALLBACK = 'https://qx.qiksolve.com/images/products/verify-hero.svg';
const SITE_ROOT = 'https://qx.qiksolve.com';
const { emitSchemaValidationPassed, emitSchemaValidationFailed } = useAnalytics();

const productImageUrl = computed(() =>
  toAbsoluteUrl(SITE_ROOT, verifyData.value?.image) ?? SOCIAL_IMAGE_FALLBACK
);

const productSchema = computed(() =>
  buildProductJsonLd({
    name: verifyData.value?.title || 'QxVerify',
    description:
      verifyData.value?.description ||
      'Streamline GMP compliance and batch record review with QxVerify.',
    url: CANONICAL_URL,
    image: productImageUrl.value,
    brandName: 'QxAIOS',
  })
);

const breadcrumbSchema = computed(() =>
  buildBreadcrumbJsonLd([
    { name: 'Home', item: `${SITE_ROOT}/` },
    { name: 'Product', item: `${SITE_ROOT}/product` },
    { name: 'Verify', item: CANONICAL_URL },
  ])
);

if (productSchema.value.schema) {
  emitSchemaValidationPassed('product', 'product');
} else {
  emitSchemaValidationFailed('product', 'product', productSchema.value.reasonCodes);
}

if (breadcrumbSchema.value) {
  emitSchemaValidationPassed('product', 'breadcrumb');
} else {
  emitSchemaValidationFailed('product', 'breadcrumb', ['missing_breadcrumb_items']);
}

/**
 * Extract markdown sections from body AST.
 * Each section is identified by its heading text.
 */
const extractSection = (content: ProductContent | null, sectionHeading: string): string => {
  if (!content?.body?.children) return '';

  const headingIndex = content.body.children.findIndex(
    (node: any) =>
      node.tag === 'h2' &&
      node.children?.[0]?.value?.toLowerCase() === sectionHeading.toLowerCase()
  );

  if (headingIndex === -1) return '';

  // Collect nodes between this heading and next heading
  const sectionNodes = [];
  for (let i = headingIndex + 1; i < content.body.children.length; i++) {
    const node = content.body.children[i];
    if (node.tag === 'h2') break; // Stop at next section
    sectionNodes.push(node);
  }

  // Convert nodes to HTML string
  return sectionNodes
    .map((node: any) => {
      if (node.tag === 'p') {
        return `<p>${node.children?.map((c: any) => c.value || '').join('')}</p>`;
      } else if (node.tag === 'ul') {
        const items = node.children
          ?.map((li: any) => `<li>${li.children?.map((c: any) => c.value || '').join('')}</li>`)
          .join('');
        return `<ul class="list-disc list-inside space-y-2 text-neutral-700">${items}</ul>`;
      }
      return '';
    })
    .join('');
};

/**
 * SEO metadata for verify product page
 * Includes Open Graph (OG), Twitter Card, and canonical URL for social sharing and SEO
 */
useSeoMeta({
  // Basic page metadata
  title: verifyData.value?.title || 'QxVerify - GMP Compliance & Batch Record Review',
  description:
    verifyData.value?.description ||
    'Streamline GMP compliance and batch record review with QxVerify.',
  keywords: verifyData.value?.keywords?.join(', '),

  // Open Graph metadata (for Facebook, LinkedIn, etc.)
  ogTitle: verifyData.value?.title || 'QxVerify',
  ogDescription:
    verifyData.value?.description ||
    'Streamline GMP compliance and batch record review with QxVerify.',
  ogImage: verifyData.value?.image
    ? `https://qx.qiksolve.com${verifyData.value.image}`
    : SOCIAL_IMAGE_FALLBACK,
  ogUrl: CANONICAL_URL,
  ogType: 'website',

  // Twitter Card metadata (for Twitter sharing)
  twitterCard: 'summary_large_image',
  twitterTitle: verifyData.value?.title || 'QxVerify',
  twitterDescription:
    verifyData.value?.description ||
    'Streamline GMP compliance and batch record review with QxVerify.',
  twitterImage: verifyData.value?.image
    ? `https://qx.qiksolve.com${verifyData.value.image}`
    : SOCIAL_IMAGE_FALLBACK,

  // Canonical URL for SEO
  canonical: CANONICAL_URL,
});

useHead({
  link: [
    {
      rel: 'canonical',
      href: CANONICAL_URL,
    },
  ],
  script: [
    ...(productSchema.value.schema ? [createJsonLdScript(productSchema.value.schema)] : []),
    ...(breadcrumbSchema.value ? [createJsonLdScript(breadcrumbSchema.value)] : []),
  ],
});
</script>

<template>
  <ProductPageTemplate class="theme-verify">
    <!-- Hero Section -->
    <template #hero>
      <div class="text-center space-y-6">
        <h1 class="text-4xl sm:text-5xl md:text-6xl font-bold text-product tracking-tight">
          {{ verifyData?.title || 'QxVerify' }}
        </h1>
        <p class="text-lg sm:text-xl text-neutral-600 max-w-3xl mx-auto">
          {{ verifyData?.description || 'Streamline GMP compliance and batch record review.' }}
        </p>
        <div
          v-if="verifyData?.image"
          class="mt-8"
        >
          <NuxtImg
            :src="verifyData.image"
            :alt="`${verifyData.title} hero illustration`"
            fetchpriority="high"
            format="webp"
            sizes="xs:100vw sm:448px"
            class="mx-auto max-w-md w-full h-auto"
          />
        </div>
        <!-- Hero CTA -->
        <div class="mt-8">
          <BookingsCTA
            v-if="bookingsAvailable"
            button-text="Book a Demo"
            :bookings-url="appConfig?.bookings?.url || '/contact/'"
            variant="primary"
            location="hero"
            product="verify"
            data-product="verify"
          />
          <FormFallbackCTA
            v-else
            button-text="Contact Sales"
            form-url="/contact"
            fallback-reason="api_unavailable"
            location="hero"
            product="verify"
          />
        </div>
      </div>
    </template>

    <!-- Problem Section -->
    <template #problem>
      <div class="prose prose-lg max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-neutral-900 mb-4">
          The Problem
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="extractSection(verifyData, 'problem')" />
      </div>
    </template>

    <!-- Solution Section -->
    <template #solution>
      <div class="prose prose-lg max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-neutral-900 mb-4">
          The Solution
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="extractSection(verifyData, 'solution')" />
      </div>
    </template>

    <!-- How It Works / Benefits Section -->
    <template #howItWorks>
      <div class="prose prose-lg max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-neutral-900 mb-4">
          Benefits
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="extractSection(verifyData, 'benefits')" />
        <!-- Mid-page CTA -->
        <div class="mt-8 text-center">
          <BookingsCTA
            v-if="bookingsAvailable"
            button-text="Schedule a Call"
            :bookings-url="appConfig?.bookings?.url || '/contact/'"
            variant="secondary"
            location="mid"
            product="verify"
            data-product="verify"
          />
          <FormFallbackCTA
            v-else
            button-text="Contact Sales"
            form-url="/contact"
            fallback-reason="api_unavailable"
            location="mid"
            product="verify"
          />
        </div>
      </div>
    </template>

    <!-- Use Cases Section -->
    <template #useCases>
      <div class="prose prose-lg max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-neutral-900 mb-4">
          Use Cases
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="extractSection(verifyData, 'use cases')" />
      </div>
    </template>

    <!-- Footer CTA -->
    <template #cta>
      <div class="text-center">
        <BookingsCTA
          v-if="bookingsAvailable"
          button-text="Get Started"
          :bookings-url="appConfig?.bookings?.url || '/contact/'"
          variant="secondary"
          location="footer"
          product="verify"
          data-product="verify"
        />
        <FormFallbackCTA
          v-else
          button-text="Get Started"
          form-url="/contact"
          fallback-reason="api_unavailable"
          location="footer"
          product="verify"
        />
      </div>
    </template>
  </ProductPageTemplate>
</template>
